import { useStation } from '@app/core/stations/hooks/useStation';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { isSocialExportEnabledForStation } from '@app/modules/social-export/hooks/useSocialExportEnabledForStation';
import { webApi } from '@mi-front/api-clients';

const ALLOW_ALL_PLATFORMS = true;

export interface AvailableExportPlatformsResult {
  /** Platforms connected for the station and enabled for social export. */
  readonly platforms: SocialPlatform[];
  /**
   * False while the station social-connections request is in flight (when {@link stationId} is set).
   * Used to avoid treating an empty connection list as "no platforms" before the response arrives.
   */
  readonly areConnectionsResolved: boolean;
}

/**
 * Returns platforms that are connected for the station and enabled for social export.
 * When {@link ALLOW_ALL_PLATFORMS} is false, excludes platforms that already have uploads
 * for the fragment. Used to show "Publicar en" dropdown options.
 */
export function useAvailableExportPlatforms(
  stationId: string | undefined,
  uploadsByPlatform: Map<string, webApi.FragmentSocialUploadItemDto[]> | undefined,
): AvailableExportPlatformsResult {
  const station = useStation(stationId ?? '');
  const connectionsQuery = webApi.useGetSocialConnectionsConnectionsForStation(stationId ?? '', {
    query: { enabled: !!stationId },
  });

  const results = connectionsQuery.data?.results ?? [];
  const platforms = socialPlatforms.all();

  const availablePlatforms = platforms.filter((platform) => {
    if (!isSocialExportEnabledForStation(station, platform)) return false;
    const connected =
      results.find((r) => r.platform.toUpperCase() === platform.id.toUpperCase())?.connected ??
      false;
    if (!connected) return false;
    if (ALLOW_ALL_PLATFORMS) return true;
    const uploads = uploadsByPlatform?.get(platform.id) ?? [];
    return uploads.length === 0;
  });

  const areConnectionsResolved = !stationId || !connectionsQuery.isLoading;

  return { platforms: availablePlatforms, areConnectionsResolved };
}
